import { createClient } from '@supabase/supabase-js';

// Enhanced debugging for environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log('=== Supabase Configuration Debug ===');
console.log('Supabase URL:', supabaseUrl ? `${supabaseUrl.substring(0, 20)}...` : 'Missing');
console.log('Supabase Anon Key:', supabaseAnonKey ? `${supabaseAnonKey.substring(0, 10)}...` : 'Missing');
console.log('Environment mode:', import.meta.env.MODE);
console.log('All env vars:', Object.keys(import.meta.env).filter(key => key.startsWith('VITE_')));

if (!supabaseUrl || !supabaseAnonKey) {
  const errorDetails = {
    url: !!supabaseUrl,
    key: !!supabaseAnonKey,
    urlValue: supabaseUrl || 'undefined',
    keyValue: supabaseAnonKey ? 'exists' : 'undefined'
  };
  console.error('Missing Supabase environment variables:', errorDetails);
  throw new Error(`Missing Supabase environment variables: ${JSON.stringify(errorDetails)}`);
}

// Validate URL format
if (!supabaseUrl.includes('supabase.co')) {
  console.error('Invalid Supabase URL format:', supabaseUrl);
  throw new Error('Invalid Supabase URL format');
}

// Validate key format (should start with 'eyJ')
if (!supabaseAnonKey.startsWith('eyJ')) {
  console.error('Invalid Supabase anon key format. Key should start with "eyJ"');
  throw new Error('Invalid Supabase anon key format');
}

console.log('Creating Supabase client...');
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

// Enhanced connection test with better error handling
const testConnection = async () => {
  console.log('Testing Supabase connection...');
  try {
    const { data, error, count } = await supabase
      .from('advisors')
      .select('count', { count: 'exact', head: true });
    
    if (error) {
      console.error('Supabase connection test failed:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      
      // Provide specific guidance based on error type
      if (error.message?.includes('Invalid API key')) {
        console.error('🚨 API Key Issue: Your VITE_SUPABASE_ANON_KEY appears to be invalid or expired.');
        console.error('💡 Solution: Check your Supabase dashboard → Project Settings → API → anon/public key');
      } else if (error.message?.includes('relation') && error.message?.includes('does not exist')) {
        console.error('🚨 Database Issue: The advisors table does not exist.');
        console.error('💡 Solution: Run your database migrations or check your database setup');
      }
    } else {
      console.log('✅ Supabase connection test successful. Advisors count:', count);
    }
    
    return { success: !error, error, count };
  } catch (networkError) {
    console.error('❌ Network error during Supabase connection test:', networkError);
    return { success: false, error: networkError, count: null };
  }
};

// Run connection test and export result
testConnection().then(result => {
  if (!result.success) {
    // Store the error for the app to handle
    (window as any).__supabaseConnectionError = result.error;
  }
});

// Database types
export type Database = {
  public: {
    Tables: {
      user_profiles: {
        Row: {
          id: string;
          preferred_name: string;
          focus_areas: string[];
          onboarding_completed: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id: string;
          preferred_name: string;
          focus_areas?: string[];
          onboarding_completed?: boolean;
        };
        Update: {
          id?: string;
          preferred_name?: string;
          focus_areas?: string[];
          onboarding_completed?: boolean;
        };
      };
      advisors: {
        Row: {
          id: string;
          name: string;
          title: string;
          description: string;
          personality_prompt: string;
          gender: 'male' | 'female';
          elevenlabs_agent_id: string | null;
          voice_settings: any;
          is_default: boolean;
          created_by: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          title: string;
          description: string;
          personality_prompt: string;
          gender?: 'male' | 'female';
          elevenlabs_agent_id?: string | null;
          voice_settings?: any;
          is_default?: boolean;
          created_by?: string | null;
        };
        Update: {
          id?: string;
          name?: string;
          title?: string;
          description?: string;
          personality_prompt?: string;
          gender?: 'male' | 'female';
          elevenlabs_agent_id?: string | null;
          voice_settings?: any;
          is_default?: boolean;
          created_by?: string | null;
        };
      };
      conversations: {
        Row: {
          id: string;
          user_id: string;
          title: string;
          advisor_ids: string[];
          conversation_type: 'individual' | 'group';
          last_message_at: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          title: string;
          advisor_ids: string[];
          conversation_type: 'individual' | 'group';
          last_message_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          title?: string;
          advisor_ids?: string[];
          conversation_type?: 'individual' | 'group';
          last_message_at?: string;
        };
      };
      messages: {
        Row: {
          id: string;
          conversation_id: string;
          sender_type: 'user' | 'advisor';
          sender_advisor_id: string | null;
          content: string;
          message_type: 'text' | 'voice_transcript' | 'system';
          metadata: any;
          created_at: string;
        };
        Insert: {
          id?: string;
          conversation_id: string;
          sender_type: 'user' | 'advisor';
          sender_advisor_id?: string | null;
          content: string;
          message_type?: 'text' | 'voice_transcript' | 'system';
          metadata?: any;
        };
        Update: {
          id?: string;
          conversation_id?: string;
          sender_type?: 'user' | 'advisor';
          sender_advisor_id?: string | null;
          content?: string;
          message_type?: 'text' | 'voice_transcript' | 'system';
          metadata?: any;
        };
      };
      voice_calls: {
        Row: {
          id: string;
          conversation_id: string;
          user_id: string;
          advisor_ids: string[];
          status: 'starting' | 'active' | 'ended' | 'failed';
          elevenlabs_conversation_id: string | null;
          duration_seconds: number;
          transcript: any;
          started_at: string;
          ended_at: string | null;
        };
        Insert: {
          id?: string;
          conversation_id: string;
          user_id: string;
          advisor_ids: string[];
          status?: 'starting' | 'active' | 'ended' | 'failed';
          elevenlabs_conversation_id?: string | null;
          duration_seconds?: number;
          transcript?: any;
          started_at?: string;
          ended_at?: string | null;
        };
        Update: {
          id?: string;
          conversation_id?: string;
          user_id?: string;
          advisor_ids?: string[];
          status?: 'starting' | 'active' | 'ended' | 'failed';
          elevenlabs_conversation_id?: string | null;
          duration_seconds?: number;
          transcript?: any;
          started_at?: string;
          ended_at?: string | null;
        };
      };
      video_calls: {
        Row: {
          id: string;
          conversation_id: string;
          user_id: string;
          advisor_id: string;
          status: 'starting' | 'active' | 'ended' | 'failed';
          tavus_conversation_id: string | null;
          tavus_room_url: string | null;
          duration_seconds: number;
          started_at: string;
          ended_at: string | null;
        };
        Insert: {
          id?: string;
          conversation_id: string;
          user_id: string;
          advisor_id: string;
          status?: 'starting' | 'active' | 'ended' | 'failed';
          tavus_conversation_id?: string | null;
          tavus_room_url?: string | null;
          duration_seconds?: number;
          started_at?: string;
          ended_at?: string | null;
        };
        Update: {
          id?: string;
          conversation_id?: string;
          user_id?: string;
          advisor_id?: string;
          status?: 'starting' | 'active' | 'ended' | 'failed';
          tavus_conversation_id?: string | null;
          tavus_room_url?: string | null;
          duration_seconds?: number;
          started_at?: string;
          ended_at?: string | null;
        };
      };
      activity_logs: {
        Row: {
          id: string;
          user_id: string | null;
          action_type: string;
          resource_type: string;
          resource_id: string | null;
          metadata: any;
          ip_address: string | null;
          user_agent: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id?: string | null;
          action_type: string;
          resource_type: string;
          resource_id?: string | null;
          metadata?: any;
          ip_address?: string | null;
          user_agent?: string | null;
        };
        Update: {
          id?: string;
          user_id?: string | null;
          action_type?: string;
          resource_type?: string;
          resource_id?: string | null;
          metadata?: any;
          ip_address?: string | null;
          user_agent?: string | null;
        };
      };
    };
  };
};

// Enhanced Auth helpers with error handling
export const signUp = async (email: string, password: string, fullName: string) => {
  try {
    // Use the current origin for the redirect URL
    const redirectUrl = `${window.location.origin}/auth/confirm`;
    console.log('Using redirect URL:', redirectUrl);
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
        emailRedirectTo: redirectUrl,
      },
    });
    return { data, error };
  } catch (error: any) {
    console.error('SignUp error:', error);
    return { data: null, error };
  }
};

export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  } catch (error: any) {
    console.error('SignIn error:', error);
    return { data: null, error };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    return { error };
  } catch (error: any) {
    console.error('SignOut error:', error);
    return { error };
  }
};

export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  } catch (error: any) {
    console.error('GetCurrentUser error:', error);
    return { user: null, error };
  }
};

// Enhanced Database helpers with better error handling
export const createUserProfile = async (userId: string, preferredName: string, focusAreas: string[]) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .insert({
        id: userId,
        preferred_name: preferredName,
        focus_areas: focusAreas,
        onboarding_completed: true,
      });
    return { data, error };
  } catch (error: any) {
    console.error('CreateUserProfile error:', error);
    return { data: null, error };
  }
};

export const getUserProfile = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    
    return { data, error };
  } catch (error: any) {
    console.error('GetUserProfile error:', error);
    return { data: null, error };
  }
};

export const getAdvisors = async () => {
  try {
    const { data, error } = await supabase
      .from('advisors')
      .select('id, name, title, description, personality_prompt, gender, elevenlabs_agent_id, voice_settings, is_default, created_by, created_at')
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: true });
    return { data, error };
  } catch (error: any) {
    console.error('GetAdvisors error:', error);
    return { data: null, error };
  }
};

export const createCustomAdvisor = async (advisor: Database['public']['Tables']['advisors']['Insert']) => {
  try {
    if (!advisor.created_by) {
      return { 
        data: null, 
        error: { message: 'User ID is required to create custom advisor' } 
      };
    }

    // Check user-specific advisor limit (4 custom max per user)
    const { data: existingAdvisors } = await supabase
      .from('advisors')
      .select('id')
      .eq('created_by', advisor.created_by)
      .eq('is_default', false);
      
    if (existingAdvisors && existingAdvisors.length >= 4) {
      return { 
        data: null, 
        error: { message: 'Maximum of 4 custom advisors allowed per user' } 
      };
    }
    
    // Get count of user's custom advisors by gender to determine which agent ID to assign
    const { data: customAdvisors } = await supabase
      .from('advisors')
      .select('id, gender')
      .eq('is_default', false)
      .eq('created_by', advisor.created_by);
      
    // Available agent IDs for custom advisors (gender-based)
    const availableAgents = {
      male: [
        'agent_01jz127ss6f0h9ytd0dhz7mh2b', // Stuart
        'agent_01jz12c1jveecs3bay22hv63ab'  // George
      ],
      female: [
        'agent_01jz128wa8fs6rcjg68r991v7t', // Cassidy
        'agent_01jz12afyefkrad58530cz2xz8'  // Laura
      ]
    };
    
    // Count existing custom advisors by gender for this user
    const customMaleCount = customAdvisors?.filter(a => a.gender === 'male').length || 0;
    const customFemaleCount = customAdvisors?.filter(a => a.gender === 'female').length || 0;
    
    // Assign agent ID based on gender and existing count (round-robin)
    let elevenlabsAgentId: string | null = null;
    
    if (advisor.gender === 'male' && customMaleCount < 2) {
      // Round-robin for male advisors: 0 -> Stuart, 1 -> George
      elevenlabsAgentId = availableAgents.male[customMaleCount];
    } else if (advisor.gender === 'female' && customFemaleCount < 2) {
      // Round-robin for female advisors: 0 -> Cassidy, 1 -> Laura
      elevenlabsAgentId = availableAgents.female[customFemaleCount];
    } else {
      return { 
        data: null, 
        error: { 
          message: `Maximum of 2 ${advisor.gender} custom advisors allowed per user` 
        } 
      };
    }
    
    // Create advisor with assigned agent ID
    const advisorWithAgentId = {
      ...advisor,
      elevenlabs_agent_id: elevenlabsAgentId
    };
    
    const { data, error } = await supabase
      .from('advisors')
      .insert(advisorWithAgentId)
      .select()
      .single();
    return { data, error };
  } catch (error: any) {
    console.error('CreateCustomAdvisor error:', error);
    return { data: null, error };
  }
};

export const getUserConversations = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        messages!inner (
          content,
          created_at
        )
      `)
      .eq('user_id', userId)
      .order('last_message_at', { ascending: false });
    return { data, error };
  } catch (error: any) {
    console.error('GetUserConversations error:', error);
    return { data: null, error };
  }
};

export const createConversation = async (conversation: Database['public']['Tables']['conversations']['Insert']) => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .insert(conversation)
      .select()
      .single();
    return { data, error };
  } catch (error: any) {
    console.error('CreateConversation error:', error);
    return { data: null, error };
  }
};

export const getConversationMessages = async (conversationId: string) => {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        advisors (
          name,
          title
        )
      `)
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    return { data, error };
  } catch (error: any) {
    console.error('GetConversationMessages error:', error);
    return { data: null, error };
  }
};

export const addMessage = async (message: Database['public']['Tables']['messages']['Insert']) => {
  try {
    const { data, error } = await supabase
      .from('messages')
      .insert(message)
      .select()
      .single();
    
    // Update conversation last_message_at
    if (!error && data) {
      await supabase
        .from('conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', message.conversation_id);
    }
    
    return { data, error };
  } catch (error: any) {
    console.error('AddMessage error:', error);
    return { data: null, error };
  }
};

// Real-time subscriptions
export const subscribeToConversationMessages = (conversationId: string, callback: (payload: any) => void) => {
  return supabase
    .channel(`messages:${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      callback
    )
    .subscribe();
};

// Activity logging
export const logActivity = async (log: Database['public']['Tables']['activity_logs']['Insert']) => {
  try {
    const { error } = await supabase
      .from('activity_logs')
      .insert(log);
    return { error };
  } catch (error: any) {
    console.error('LogActivity error:', error);
    return { error };
  }
};

// Export test function for manual testing
export const testSupabaseConnection = testConnection;